// @ts-strict-ignore
import React, { useEffect, useState } from 'react'
import type { Store } from 'redux'
import { Provider } from 'react-redux'

import { IonApp, setupIonicReact } from '@ionic/react'
import { ToastProvider } from '@mytours/components'
import { IonReactRouter } from '@ionic/react-router'

import { getStore } from 'mytours-read-common'
import setConfiguration from './modules/configuration'
import IonicNavigation from './modules/navigation'
import { mapboxInitialize } from './modules/map/mapboxManager'

/* Every Ionic stylesheet, confined to the `ionic` cascade layer — see ionic.css */
import './ionic.css'

/* Font Awesome — self-hosted (replaces kit.fontawesome.com to avoid metered bandwidth) */
import '@fortawesome/fontawesome-pro/css/fontawesome.min.css'
import '@fortawesome/fontawesome-pro/css/solid.min.css'
import '@fortawesome/fontawesome-pro/css/light.min.css'
import '@fortawesome/fontawesome-pro/css/regular.min.css'
import '@fortawesome/fontawesome-pro/css/brands.min.css'
import '@fortawesome/fontawesome-pro/css/duotone.min.css'
import './assets/fontawesome/custom-icons.css'

/* @mytours/components design tokens + the components consumed today */
import '@mytours/components/tokens.css'
import '@mytours/components/audio-transport.css'
import '@mytours/components/badge.css'
import '@mytours/components/badge-group.css'
import '@mytours/components/button.css'
import '@mytours/components/card.css'
import '@mytours/components/carousel.css'
import '@mytours/components/checkbox.css'
import '@mytours/components/collection-list-item.css'
import '@mytours/components/header-bar.css'
import '@mytours/components/icon-tile.css'
import '@mytours/components/img.css'
import '@mytours/components/input.css'
import '@mytours/components/label.css'
import '@mytours/components/list.css'
import '@mytours/components/loading.css'
import '@mytours/components/logo-overlay.css'
import '@mytours/components/modal.css'
import '@mytours/components/note.css'
import '@mytours/components/popover.css'
import '@mytours/components/progress-bar.css'
import '@mytours/components/segment.css'
import '@mytours/components/spinner.css'
import '@mytours/components/stop-row.css'
import '@mytours/components/text.css'
import '@mytours/components/thumbnail.css'
import '@mytours/components/toast.css'
import '@mytours/components/sections/badge-group.css'
import '@mytours/components/sections/link-group.css'
import '@mytours/components/sections/media.css'
import '@mytours/components/sections/media-group.css'
import '@mytours/components/sections/opening-times.css'
import '@mytours/components/sections/pricing.css'
import '@mytours/components/sections/quiz-question.css'
import '@mytours/components/sections/quiz-score.css'
import '@mytours/components/sections/slideshow.css'
import '@mytours/components/sections/text.css'

import * as Sentry from '@sentry/react'
import { Elements } from '@stripe/react-stripe-js'

import './App.less'
import './less/mytours.less'
import IpGateComponent from './modules/home/components/IpGate'
import { history } from './modules/navigation/history'
import { shouldShowAppBanner } from './modules/common/functions'
import { AppBannerContainer } from './modules/home/components/AppBannerContainer'
import { ErrorBoundaryFallback } from 'mobile_web/modules/home/components/ErrorBoundaryFallback'
import { isIOSSafariBrowser } from './modules/platform'
import { loadStripeForProject } from './modules/payments/stripe'
import classNames from 'classnames'

setupIonicReact()

mapboxInitialize()

setConfiguration()
const stripePromise = loadStripeForProject()

// Deep linking feature flag, declared in views/layouts/mobile_web.html.erb
declare const deepLinkingAppleEnabled

const App: React.FC<Record<string, never>> = () => {
	const [store, setStore] = useState<Store>()
	const [appBannerShown, setAppBannerShown] = useState<boolean>(deepLinkingAppleEnabled && shouldShowAppBanner())
	const [appBannerVisible, setAppBannerVisible] = useState(true)

	useEffect(() => {
		function handleResize() {
			setAppBannerShown(shouldShowAppBanner())
		}
		if (deepLinkingAppleEnabled && isIOSSafariBrowser()) {
			window.addEventListener('resize', handleResize)
			return () => {
				window.removeEventListener('resize', handleResize)
			}
		}
	}, [])

	useEffect(() => {
		setStore(getStore())
	}, [])

	if (!store) {
		return <div />
	}

	return (
		<Sentry.ErrorBoundary fallback={<ErrorBoundaryFallback />}>
			<Provider store={store}>
				<Elements stripe={stripePromise}>
					<ToastProvider layout="centered" closeButton={false} pauseOnInteraction={false}>
						<AppBannerContainer close={() => setAppBannerVisible(false)} visible={appBannerShown && appBannerVisible} />
						<IonApp className={classNames({ 'app-banner-visible': appBannerShown && appBannerVisible })}>
							<IonReactRouter history={history}>
								<IonicNavigation />
								<IpGateComponent />
							</IonReactRouter>
						</IonApp>
					</ToastProvider>
				</Elements>
			</Provider>
		</Sentry.ErrorBoundary>
	)
}

export default App
